import Papa from "papaparse";
import { formatDateForFilename } from "./utils/formatting";

// timestampをYYYY-MM-DD HH:MM:SS形式にフォーマットする関数
const formatTimestamp = (timestamp) => {
  if (!timestamp) return ""; // nullやundefinedの場合は空文字を返す
  const date = new Date(timestamp.seconds * 1000); // Firebaseのtimestampの場合
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

// 注文CSVの列定義。ヘッダ(label)と値(value)の対応を必ずこの1箇所で定義すること。
// かつてヘッダ配列とデータ配列を別々に管理していた際、片側だけの行削除により
// 「納入先事業所コード」列に備考が出力される列ズレが発生した(2024/10/25〜2026/7/16)。
// この構造では列の追加・削除が常にラベルと値のペアで行われるため、同種のズレは起きない。
// 列の並びは export.test.js が固定している(変更時はテストも更新すること)。
const ORDER_CSV_COLUMNS = [
  { label: "カートID", value: (order) => order.order_id || "" },
  { label: "ドキュメントID", value: (order) => order.id || "" },
  { label: "注文番号", value: (order) => order.order_number || "" },
  { label: "発注担当者社員番号", value: (order) => order.order_user_id || "" },
  { label: "発注担当者名", value: (order) => order.order_user_name || "" },
  {
    label: "発注担当者部署コード",
    value: (order) => order.order_user_dep_id || "",
  },
  {
    label: "発注担当者部署名",
    value: (order) => order.order_user_dep_name || "",
  },
  {
    label: "発注担当者メールアドレス",
    value: (order) => order.order_user_email || "",
  },
  { label: "発注拠点コード", value: (order) => order.order_base_id || "" },
  { label: "発注拠点名", value: (order) => order.order_base_name || "" },
  {
    label: "発注部署コード",
    value: (order) => order.order_department_id || "",
  },
  {
    label: "発注部署名",
    value: (order) => order.order_department_name || "",
  },
  { label: "購入依頼者有無", value: (order) => order.billing_user_flg || "" },
  {
    label: "購入依頼者社員番号",
    value: (order) => order.billing_user_id || "",
  },
  { label: "購入依頼者名", value: (order) => order.billing_user_name || "" },
  {
    label: "購入依頼者部署コード",
    value: (order) => order.billing_user_dep_id || "",
  },
  {
    label: "購入依頼者部署名",
    value: (order) => order.billing_user_dep_name || "",
  },
  { label: "仕入先有無", value: (order) => order.billing_company_flg || "" },
  { label: "仕入先コード", value: (order) => order.billing_company_id || "" },
  {
    label: "仕入先名",
    value: (order) => order.billing_company_name_full || "",
  },
  { label: "仕入先略称", value: (order) => order.billing_company_name || "" },
  {
    label: "注文に関する備考",
    value: (order) => order.remark_on_order || "",
  },
  {
    label: "納入先事業所コード",
    value: (order) => order.delivery_office_id || "",
  },
  {
    label: "納入先事業所名",
    value: (order) => order.delivery_office_name || "",
  },
  {
    label: "納入先事業所住所1",
    value: (order) => order.delivery_office_address_main || "",
  },
  {
    label: "納入先事業所住所2",
    value: (order) => order.delivery_office_address_sub || "",
  },
  {
    label: "納入先事業所電話番号",
    value: (order) => order.delivery_office_tell || "",
  },
  { label: "納入先部署", value: (order) => order.delivery_department || "" },
  { label: "カート単位の合計金額", value: (order) => order.amount },
  {
    label: "カート単位の控除（負担）金額",
    value: (order) => order.deduction_amount,
  },
  { label: "商品ID", value: (order) => order.product_id || "" },
  { label: "カテゴリID", value: (order) => order.category_id || "" },
  { label: "カテゴリ名", value: (order) => order.category_name || "" },
  { label: "商品説明", value: (order) => order.description || "" },
  { label: "社内番号", value: (order) => order.idin || "" },
  { label: "社外番号", value: (order) => order.idout || "" },
  { label: "商品名", value: (order) => order.name || "" },
  { label: "価格", value: (order) => order.price },
  { label: "数量", value: (order) => order.quantity },
  { label: "合計金額", value: (order) => order.totalPrice || "" },
  { label: "給与控除区分", value: (order) => order.deduction_id },
  { label: "給与控除区分名", value: (order) => order.deduction_name || "" },
  { label: "給与控除金額", value: (order) => order.deduction_price },
  { label: "サイズ", value: (order) => order.size || "" },
  { label: "メーカーID", value: (order) => order.maker_id || "" },
  { label: "メーカー名", value: (order) => order.maker_name || "" },
  { label: "販売先ID", value: (order) => order.shop_id || "" },
  { label: "販売店名", value: (order) => order.shop_name || "" },
  { label: "販売店FAX", value: (order) => order.shop_fax || "" },
  { label: "販売店宛名", value: (order) => order.shop_to || "" },
  { label: "販売店CC", value: (order) => order.shop_cc || "" },
  { label: "刺繍", value: (order) => order.embroidery || "" },
  { label: "備考", value: (order) => order.remarks || "" },
  {
    label: "カート追加日時",
    value: (order) => formatTimestamp(order.added_at),
  },
  { label: "状態", value: (order) => order.status || "" },
  { label: "注文日時", value: (order) => formatTimestamp(order.placed_at) },
  { label: "発注日時", value: (order) => formatTimestamp(order.ordered_at) },
  { label: "検収日時", value: (order) => formatTimestamp(order.received_at) },
  {
    label: "キャンセル日時",
    value: (order) => formatTimestamp(order.canceled_at),
  },
  {
    label: "控除適用成功",
    value: (order) => order.deduction_is_valid || "",
  },
  { label: "ログインユーザUID", value: (order) => order.login_user_uid || "" },
  {
    label: "ログインユーザ部署コード",
    value: (order) => order.login_user_dep_id || "",
  },
  {
    label: "ログインユーザ部署名",
    value: (order) => order.login_user_dep_name || "",
  },
  {
    label: "ログインユーザメールアドレス",
    value: (order) => order.login_user_email || "",
  },
  {
    label: "ログインユーザ社員番号",
    value: (order) => order.login_user_id || "",
  },
  {
    label: "ログインユーザ社員名",
    value: (order) => order.login_user_name || "",
  },
  {
    label: "ログインユーザ権限",
    value: (order) => order.login_user_role || "",
  },
  {
    label: "最終更新日時",
    value: (order) => formatTimestamp(order.updated_at),
  },
  { label: "作成日時", value: (order) => formatTimestamp(order.created_at) },
  {
    label: "最終更新者メールアドレス",
    value: (order) => order.update_user_email || "",
  },
  {
    label: "作成者メールアドレス",
    value: (order) => order.create_user_email || "",
  },
  {
    // どのビルドのアプリが作成した注文かを示す(2026/7/16以降の注文に入る。過去分は空欄)
    label: "作成ビルドID",
    value: (order) => order.client_build_id || "",
  },
];

// ヘッダと行データを列定義から組み立てる(純関数。export.test.jsのテスト対象)
export const buildOrdersCsvTable = (orders) => ({
  fields: ORDER_CSV_COLUMNS.map((column) => column.label),
  data: orders.map((order) =>
    ORDER_CSV_COLUMNS.map((column) => column.value(order))
  ),
});

// CSV文字列を組み立ててダウンロードさせる共通処理
const downloadCsv = (table, filenamePrefix) => {
  const bom = String.fromCharCode(0xfeff); // BOM(U+FEFF)の追加
  const csv = bom + Papa.unparse(table);

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `${filenamePrefix}_${formatDateForFilename(new Date())}.csv`
  );
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const exportOrdersToCsv = (orders) => {
  downloadCsv(buildOrdersCsvTable(orders), "orders");
};

export const exportBjyoCsv = (orders) => {
  // ヘッダの定義
  const headers = ["社員番号", "社員名", "給与控除額"];
  const csvData = orders.map((order) => [
    // データのマッピング
    order.billing_user_id || "",
    order.billing_user_name || "",
    order.deduction_price || "",
  ]);
  downloadCsv({ fields: headers, data: csvData }, "bjyo");
};
